import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import { Alert, Button, message } from 'antd';
import { FunctionComponent, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { downloadSpreadsheetFile } from '../../api/documentProjectApi';

interface ViewerState {
    title?: string;
    viewerUrl?: string;
    blobUrl?: string;
    kind?: 'pdf' | 'spreadsheet';
}

const preventContextMenu = (e: React.MouseEvent) => e.preventDefault();

/** Sandbox breaks Chrome's built-in PDF viewer layout; keep it for Office embed only (blocks window.open pop-out). */
const VIEWER_IFRAME_SANDBOX = 'allow-scripts allow-same-origin allow-forms allow-presentation';

function spreadsheetDownloadFileName(title: string): string {
    const trimmed = title.trim() || 'spreadsheet';
    return /\.(xlsx|xls|csv)$/i.test(trimmed) ? trimmed : `${trimmed}.xlsx`;
}

const ProjectDocumentViewer: FunctionComponent = () => {
    const history = useHistory();
    const location = useLocation<ViewerState>();
    const { fileId: fileIdParam } = useParams<{ fileId: string }>();
    const fileId = Number.parseInt(fileIdParam || '', 10);
    const { title = 'Document', viewerUrl, blobUrl, kind } = location.state || {};
    const [downloading, setDownloading] = useState(false);

    // PDF: trim Chrome PDF UI (#toolbar); Office embed URL is built in DocumentsList.
    const iframeSrc = viewerUrl
        ? kind === 'pdf'
            ? `${viewerUrl.replace(/#.*$/, '')}#toolbar=0&navpanes=0`
            : viewerUrl
        : blobUrl || null;

    const handleDownloadSpreadsheet = async () => {
        if (!Number.isFinite(fileId) || fileId < 1) {
            message.error('Invalid document.');
            return;
        }
        setDownloading(true);
        try {
            const blob = await downloadSpreadsheetFile(fileId);
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = spreadsheetDownloadFileName(title);
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);
            message.success('Download started.');
        } catch {
            message.error('Could not download. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    if (!iframeSrc) {
        return (
            <div className="documents-page" onContextMenu={preventContextMenu}>
                <div className="documents-page__inner">
                    <Alert type="warning" message="Document URL not found." showIcon />
                    <Button type="link" onClick={() => history.push('/documents')} className="documents-page__back">
                        Back to Deal Room
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div
            className="documents-page documents-page--viewer"
            onContextMenu={preventContextMenu}
        >
            <div className="documents-page__toolbar">
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => history.push('/documents')}
                    className="documents-page__toolbar-btn"
                >
                    Back to Deal Room
                </Button>
                <span className="documents-page__toolbar-title">{title}</span>
                {kind === 'spreadsheet' && (
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        className="documents-page__toolbar-open"
                        loading={downloading}
                        onClick={handleDownloadSpreadsheet}
                    >
                        Download
                    </Button>
                )}
            </div>
            <div
                className={`documents-page__frame-wrap${
                    kind === 'pdf' ? ' documents-page__frame-wrap--pdf' : ''
                }`}
            >
                <iframe
                    title={title}
                    src={iframeSrc}
                    className="documents-page__iframe"
                    allow="fullscreen"
                    {...(kind === 'spreadsheet'
                        ? { sandbox: VIEWER_IFRAME_SANDBOX }
                        : {})}
                    referrerPolicy="strict-origin-when-cross-origin"
                />
                {kind === 'spreadsheet' && (
                    <div
                        className="documents-page__share-blocker"
                        role="presentation"
                        aria-hidden="true"
                        tabIndex={-1}
                        title="Sharing and external open are disabled for this document."
                        onMouseDownCapture={(e) => e.preventDefault()}
                    />
                )}
            </div>
        </div>
    );
};

export default ProjectDocumentViewer;
